import { useMemo } from 'react';

import Decimal from 'decimal.js';

import { Address } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';

import { normalizePositionManagerInfo } from '@helpers/vaults/positionManager.helper';
import { normalizePercentForContact } from '@helpers/vaults/priceRange.helpers';

import { toBN } from '@utils/formaters.utils';
import { isAddressesEqual } from '@utils/tokens.utils';

import { CHAIN_ID, ZERO_ADDRESS } from '@constants/common/chain.constants';
import {
  LIQUIDITY_HELPER_BY_DEX_CODE,
  LiquidityHelperType,
} from '@constants/vaults/contracts.constants';
import { V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';

import abiCommon from '@abi/vault/LiquidityHelperV3';
import abiAerodromeCl from '@abi/vault/LiquidityHelperV3AerodromeCl';
import abiShadow from '@abi/vault/LiquidityHelperV3Shadow';

import { TokensDto } from '@dto/vaults/tokens.dto';
import { VaultFactoryDto } from '@dto/vaults/vault-factory.dto';
import { LegacyVaultsDto, VaultDto } from '@dto/vaults/vaults.dto';

import { PositionManagerInfoItem } from '@app-types/vaults/positionManagerInfo.types';
import { VaultFarmsType } from '@app-types/vaults/vault.types';

type PositionManagerInfoParamType = {
  vaultFarms: VaultFarmsType[];
  vaultFactory: VaultFactoryDto;
  tokens: TokensDto | undefined;
  isFullInfo?: boolean;
  vault: LegacyVaultsDto;
};

export const usePositionManagerInfo = ({
  vaultFarms,
  vault,
  vaultFactory,
  tokens,
  isFullInfo = false,
}: PositionManagerInfoParamType) => {
  const v3Farm = useMemo(
    () =>
      vaultFarms.filter((farm) =>
        V3_TOKENS_TYPE.includes(farm.farmInfo.stakingTokenType),
      ),
    [vaultFarms],
  );

  const isNoTokenIds = useMemo(
    () => v3Farm.some((item) => item.farm.poolV3PositionId === 0),
    [v3Farm],
  );

  const positionManagerInfoCalls = useMemo(
    () =>
      v3Farm.map((farm) => {
        const { stakingTokenAddress, dexFactory } = farm.farmInfo;
        const { poolV3PositionId } = farm.farm;
        const liquidityHelper =
          LIQUIDITY_HELPER_BY_DEX_CODE[vault.chainId][Number(dexFactory.code)];

        let abi;

        switch (liquidityHelper.type) {
          case LiquidityHelperType.AerodromeCl:
            abi = abiAerodromeCl;
            break;
          case LiquidityHelperType.Shadow:
            abi = abiShadow;
            break;
          case LiquidityHelperType.Common:
            abi = abiCommon;
            break;
          default:
            abi = abiCommon;
            break;
        }

        return {
          address: liquidityHelper.address as Address,
          abi,
          functionName: 'positionManagerInfo' as const,
          args: [
            [
              {
                positionManager: stakingTokenAddress as Address,
                tokenId: BigInt(poolV3PositionId || 0),
              },
            ],
          ] as const,
          chainId: vault.chainId,
        };
      }),
    [v3Farm, vault],
  );

  const { data, refetch } = useReadContracts({
    allowFailure: false,
    contracts: positionManagerInfoCalls,
    query: {
      enabled:
        isFullInfo &&
        !!positionManagerInfoCalls.length &&
        toBN(vault.tvlUsd).gt(0) &&
        !isNoTokenIds,
    },
  });

  const positionManagerInfo: Record<number, PositionManagerInfoItem> =
    useMemo(() => {
      if (!data || !tokens) return {};

      if (data.some((item) => isAddressesEqual(item[0].pool, ZERO_ADDRESS)))
        return {};

      return data.reduce((acc, item, index) => {
        const { farm } = v3Farm[index];
        const { farmId } = farm;
        const tokenId = farm.poolV3PositionId;

        return {
          ...acc,
          [farmId]: normalizePositionManagerInfo({
            tokens,
            vaultFactory,
            positionManagerInfo: item[0],
            tokenId: BigInt(tokenId || 0),
          }),
        };
      }, {});
    }, [data, tokens, v3Farm, vaultFactory]);

  return {
    positionManagerInfo,
    refetch,
  };
};

type UserNftInfoParamType = {
  vaultFarms: VaultFarmsType[];
  vaultFactory: VaultFactoryDto;
  tokens: TokensDto | undefined;
  tokensIds: bigint[];
  isEnableNftDeposit: boolean;
  isFullInfo?: boolean;
};

export const useUserNftInfo = ({
  tokensIds,
  vaultFarms,
  isFullInfo = false,
  isEnableNftDeposit,
  vaultFactory,
  tokens,
}: UserNftInfoParamType) => {
  const nonfungiblePositionManagerAddress = useMemo(
    () => vaultFarms[0]?.farmInfo?.stakingTokenAddress as Address,
    [vaultFarms],
  );

  const currentDex = useMemo(
    () => vaultFarms[0]?.farmInfo?.dexFactory,
    [vaultFarms],
  );

  const liquidityHelper = useMemo(() => {
    if (!currentDex || !isFullInfo) return '';

    return LIQUIDITY_HELPER_BY_DEX_CODE[currentDex.chainId as CHAIN_ID][
      Number(currentDex.code)
    ]?.address;
  }, [currentDex, isFullInfo]);

  const positionManagerInfoArgs = useMemo(
    () =>
      tokensIds.map((item) => ({
        positionManager: nonfungiblePositionManagerAddress,
        tokenId: item,
      })),
    [tokensIds, nonfungiblePositionManagerAddress],
  );

  const { data, refetch, isPending } = useReadContract({
    address: liquidityHelper as Address,
    abi: abiCommon,
    functionName: 'positionManagerInfo',
    args: [
      positionManagerInfoArgs as {
        positionManager: Address;
        tokenId: bigint;
      }[],
    ],
    chainId: vaultFactory?.chainId,
    query: {
      enabled:
        isFullInfo &&
        isEnableNftDeposit &&
        !!positionManagerInfoArgs.length &&
        !!vaultFactory &&
        !!liquidityHelper,
    },
  });

  const userNftInfo = useMemo(() => {
    if (!data || !tokens || !vaultFarms.length) return [];

    const { stakingToken } = vaultFarms[0].farmInfo;

    const userTokenIds: bigint[] = [];

    const userStakingTokensNft = data.filter((item, index) => {
      if (
        isAddressesEqual(item.token0, stakingToken.token0) &&
        isAddressesEqual(item.token1, stakingToken.token1)
      ) {
        userTokenIds.push(positionManagerInfoArgs[index].tokenId);
      }
      return (
        isAddressesEqual(item.token0, stakingToken.token0) &&
        isAddressesEqual(item.token1, stakingToken.token1)
      );
    });

    const normalizeUserStakingTokensNft = userStakingTokensNft.map(
      (item, index) =>
        normalizePositionManagerInfo({
          tokens,
          vaultFactory,
          positionManagerInfo: item,
          tokenId: userTokenIds[index],
        }),
    );

    return normalizeUserStakingTokensNft.filter(
      (item) => !item.liquidity.isZero(),
    );
  }, [data, vaultFarms, tokens, vaultFactory, positionManagerInfoArgs]);

  return {
    userNftInfo,
    refetch,
    isPending,
  };
};

interface UseWeightAmountsLiquidityParams {
  farm: VaultFarmsType;
  min: Decimal;
  max: Decimal;
}

export const useWeightAmountsLiquidity = ({
  min,
  max,
  farm,
}: UseWeightAmountsLiquidityParams) => {
  const low = normalizePercentForContact(min);
  const up = normalizePercentForContact(max);

  const isEnabled = useMemo(() => !!farm && !!low && !!up, [low, up, farm]);

  const currentDex = useMemo(() => farm?.farmInfo?.dexFactory, [farm]);

  const liquidityHelper = useMemo(() => {
    if (!currentDex) return '';

    return LIQUIDITY_HELPER_BY_DEX_CODE[currentDex.chainId as CHAIN_ID][
      Number(currentDex.code)
    ]?.address;
  }, [currentDex]);

  const nonfungiblePositionManagerAddress = useMemo(
    () => farm?.farmInfo?.poolV3 as Address,
    [farm],
  );

  const { data, isPending } = useReadContract({
    address: liquidityHelper as Address,
    abi: abiCommon,
    functionName: 'calculateWeightAmountsPerUnitLiquidity' as const,
    args: [nonfungiblePositionManagerAddress, low, up],
    chainId: farm.farmInfo?.chainId,
    query: {
      enabled: isEnabled,
    },
  });

  return {
    amount0: data ? data[0] : BigInt(0),
    amount1: data ? data[1] : BigInt(0),
    isPending: isPending && isEnabled,
  };
};
